"use client";

const FilterSidebar = ({
  filters,
  onFiltersChange,
  onResetFilters,
  onApplyFilters,
}) => {
  const handleGenderChange = (gender) => {
    onFiltersChange({ ...filters, gender });
  };

  const handleAgeChange = (age) => {
    onFiltersChange({ ...filters, age });
  };

  const handleDistanceMinChange = (distanceMin) => {
    if (distanceMin <= filters.distanceMax - 1) {
      onFiltersChange({ ...filters, distanceMin });
    }
  };

  const handleDistanceMaxChange = (distanceMax) => {
    if (distanceMax >= filters.distanceMin + 1) {
      onFiltersChange({ ...filters, distanceMax });
    }
  };

  const resetFilters = () => {
    onFiltersChange({
      gender: "Both",
      age: 29,
      distanceMin: 1,
      distanceMax: 50,
    });
  };

  return (
    <div className="p-6 h-full overflow-y-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-2xl font-bold text-gray-900">Filters</h2>
          <button
            onClick={() => {
              resetFilters();
              onResetFilters();
            }}
            className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            Reset
          </button>
        </div>
        <p className="text-sm text-gray-600">Find your perfect match</p>
      </div>

      {/* Gender Selection */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Gender Preference
        </h3>
        <div className="space-y-3">
          {["Boy", "Girl", "Both"].map((gender) => (
            <button
              key={gender}
              onClick={() => handleGenderChange(gender)}
              className={`w-full px-4 py-3 rounded-xl font-medium transition-all duration-200 text-left ${
                filters.gender === gender
                  ? "bg-[#7B189F] text-white shadow-lg"
                  : "bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100"
              }`}
            >
              {gender}
            </button>
          ))}
        </div>
      </div>

      {/* Age Slider */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Age Range</h3>
          <span className="text-lg font-semibold text-purple-600">
            {filters.age} years
          </span>
        </div>
        <div className="relative px-2">
          <div className="relative h-2 bg-gray-200 rounded-full">
            <div
              className="absolute h-2 bg-purple-600 rounded-full transition-all duration-200"
              style={{ width: `${((filters.age - 18) / (50 - 18)) * 100}%` }}
            />
            <input
              type="range"
              min="18"
              max="50"
              value={filters.age}
              onChange={(e) => handleAgeChange(Number.parseInt(e.target.value))}
              className="absolute inset-0 w-full h-2 opacity-0 cursor-pointer"
            />
            <div
              className="absolute w-5 h-5 bg-[#7B189F] border-2 border-white rounded-full shadow-lg transform -translate-y-1.5 transition-all duration-200 hover:scale-110"
              style={{
                left: `calc(${((filters.age - 18) / (50 - 18)) * 100}% - 10px)`,
              }}
            />
          </div>
          <div className="flex justify-between text-xs text-gray-500 mt-2">
            <span>18</span>
            <span>50</span>
          </div>
        </div>
      </div>

      {/* Distance Slider */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Distance</h3>
          <span className="text-lg font-semibold text-[#7B189F]">
            {filters.distanceMin}-{filters.distanceMax} km
          </span>
        </div>
        <div className="relative px-2">
          <div className="relative h-2 bg-gray-200 rounded-full">
            <div
              className="absolute h-2 bg-[#7B189F] rounded-full transition-all duration-200"
              style={{
                left: `${((filters.distanceMin - 1) / (50 - 1)) * 100}%`,
                width: `${
                  ((filters.distanceMax - filters.distanceMin) / (50 - 1)) * 100
                }%`,
              }}
            />

            {/* Min Range Input */}
            <input
              type="range"
              min="1"
              max="50"
              value={filters.distanceMin}
              onChange={(e) =>
                handleDistanceMinChange(Number.parseInt(e.target.value))
              }
              className="absolute inset-0 w-full h-2 opacity-0 cursor-pointer"
            />

            {/* Max Range Input */}
            <input
              type="range"
              min="1"
              max="50"
              value={filters.distanceMax}
              onChange={(e) =>
                handleDistanceMaxChange(Number.parseInt(e.target.value))
              }
              className="absolute inset-0 w-full h-2 opacity-0 cursor-pointer"
            />

            {/* Min Thumb */}
            <div
              className="absolute w-5 h-5 bg-[#7B189F] border-2 border-white rounded-full shadow-lg transform -translate-y-1.5 transition-all duration-200 hover:scale-110 z-10"
              style={{
                left: `calc(${
                  ((filters.distanceMin - 1) / (50 - 1)) * 100
                }% - 10px)`,
              }}
            />

            {/* Max Thumb */}
            <div
              className="absolute w-5 h-5 bg-[#7B189F] border-2 border-white rounded-full shadow-lg transform -translate-y-1.5 transition-all duration-200 hover:scale-110 z-10"
              style={{
                left: `calc(${
                  ((filters.distanceMax - 1) / (50 - 1)) * 100
                }% - 10px)`,
              }}
            />
          </div>
          <div className="flex justify-between text-xs text-gray-500 mt-2">
            <span>1 km</span>
            <span>50 km</span>
          </div>
        </div>
      </div>

      {/* Apply Button */}
      <div className="mt-auto pt-6 border-t border-gray-200">
        <button
          onClick={onApplyFilters}
          className="w-full bg-[#7B189F] hover:bg-purple-800 text-white font-semibold py-4 rounded-xl text-lg transition-colors shadow-lg hover:shadow-xl"
        >
          Apply Filters
        </button>
      </div>

      {/* Active Filters Count */}
      <div className="mt-4 text-center">
        <span className="text-sm text-gray-500">
          {(filters.gender !== "Both" ? 1 : 0) +
            (filters.age !== 25 ? 1 : 0) +
            (filters.distanceMin !== 1 || filters.distanceMax !== 50
              ? 1
              : 0)}{" "}
          filters active
        </span>
      </div>
    </div>
  );
};

export default FilterSidebar;
